// File path: /core/frontend/src/services/subscriptionService.ts
// Subscription Service - API client for module subscription management

import axios from 'axios';
import { Auth } from 'aws-amplify';
import type { ModuleId, ModuleTier, ModuleSubscription, OrganizationSubscription } from '../config/moduleRegistry';
import { retryOnceOn503 } from './retryOn503';

// API base URL - use environment variable or default to dev API
// This matches the endpoint configured in aws-config.js
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dev.api.lumilearn.app';

// Create axios instance
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

// Add auth interceptor
apiClient.interceptors.request.use(async (config) => {
  try {
    const session = await Auth.currentSession();
    const token = session.getAccessToken().getJwtToken();
    config.headers.Authorization = `Bearer ${token}`;
  } catch (error) {
    console.log('No authentication token available');
  }
  return config;
});

// 503 (C-503UI): wait Retry-After, retry once; a second 503 rejects with a readable message.
// SubscriptionContext grants no module when this call fails, so a cold-cluster 503 gets the same
// single retry as the other clients.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => (error?.response?.status === 503 ? retryOnceOn503(apiClient, error) : Promise.reject(error)),
);

// Response types
export interface UserModulesResponse {
  modules: ModuleSubscription[];
  user_id: string;
}

export interface ModuleAccessResponse {
  has_access: boolean;
  module_id: string;
  organization_id?: string;
  reason?: string;
}

export interface AvailableModulesResponse {
  modules: Array<{
    id: string;
    name: string;
    description: string;
    version: string;
    icon: string;
    color: string;
    is_active: boolean;
    display_order: number;
  }>;
  total: number;
}

class SubscriptionService {
  private basePath = '/subscriptions';

  /**
   * Get all available modules in the system
   */
  async getAvailableModules(): Promise<AvailableModulesResponse> {
    const response = await apiClient.get(`${this.basePath}/modules`);
    return response.data;
  }

  /**
   * Get modules accessible to the current user
   */
  async getUserModules(): Promise<UserModulesResponse> {
    const response = await apiClient.get(`${this.basePath}/user`);
    return response.data;
  }

  /**
   * Check if user has access to a specific module
   */
  async checkModuleAccess(moduleId: ModuleId): Promise<ModuleAccessResponse> {
    const response = await apiClient.get(`${this.basePath}/check/${moduleId}`);
    return response.data;
  }

  /**
   * Get subscription details for an organization
   */
  async getOrganizationSubscription(organizationId: string): Promise<{ subscription: OrganizationSubscription }> {
    const response = await apiClient.get(`${this.basePath}/organization/${organizationId}`);
    return response.data;
  }

  /**
   * Create a new subscription (admin only)
   */
  async createSubscription(data: {
    organization_id: string;
    organization_type: 'Program' | 'District' | 'School';
    license_type?: 'saas' | 'dedicated' | 'enterprise';
    status?: 'active' | 'trial' | 'suspended' | 'expired';
    modules?: Array<{
      module_id: ModuleId;
      tier?: ModuleTier;
      enabled_features?: string[];
    }>;
  }): Promise<{ subscription_id: string }> {
    const response = await apiClient.post(this.basePath, data);
    return response.data;
  }

  /**
   * Update a subscription (admin only)
   */
  async updateSubscription(subscriptionId: string, data: {
    license_type?: 'saas' | 'dedicated' | 'enterprise';
    status?: 'active' | 'trial' | 'suspended' | 'expired';
    end_date?: string;
    max_users?: number;
    max_students?: number;
  }): Promise<{ subscription: OrganizationSubscription }> {
    const response = await apiClient.put(`${this.basePath}/${subscriptionId}`, data);
    return response.data;
  }

  /**
   * Add a module to a subscription (admin only)
   */
  async addModuleToSubscription(subscriptionId: string, data: {
    module_id: ModuleId;
    tier?: ModuleTier;
    enabled_features?: string[];
  }): Promise<{ subscription_module: ModuleSubscription }> {
    const response = await apiClient.post(`${this.basePath}/${subscriptionId}/modules`, data);
    return response.data;
  }

  /**
   * Remove a module from a subscription (admin only)
   */
  async removeModuleFromSubscription(subscriptionId: string, moduleId: ModuleId): Promise<void> {
    await apiClient.delete(`${this.basePath}/${subscriptionId}/modules/${moduleId}`);
  }
}

// Export singleton instance
const subscriptionService = new SubscriptionService();
export default subscriptionService;

// The service's axios instance (tests drive it through a mocked transport)
export { apiClient as subscriptionApiClient };
